import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserView } from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Users,
  Clock,
  UserCheck,
  Monitor,
  Search,
  MoreHorizontal,
  CheckCircle,
  Key,
  Settings,
  ShieldCheck,
  ShieldX,
  Ban,
} from 'lucide-react';

interface AdminStats {
  totalUsers: number;
  pendingApproval: number;
  activeUsers: number;
  activeSessions: number;
}

export default function AdminPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserView | null>(null);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [maxSessionsInput, setMaxSessionsInput] = useState(1);
  const [maxCpuInput, setMaxCpuInput] = useState(2);
  const [maxMemoryInput, setMaxMemoryInput] = useState(4);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsAmountInput, setCreditsAmountInput] = useState(0);
  const [creditsOperationInput, setCreditsOperationInput] = useState<
    'set' | 'add'
  >('add');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [userToRevoke, setUserToRevoke] = useState<string | null>(null);
  const [userToToggleRole, setUserToToggleRole] = useState<{
    id: string;
    currentRole: string;
  } | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchData();
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        fetch(`/api/admin/users?search=${encodeURIComponent(search)}`, {
          credentials: 'include',
        }),
        fetch('/api/admin/stats', { credentials: 'include' }),
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.data.users);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error(t('admin:errors.loadAdminDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/approve`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('admin:messages.userApproved'));
        fetchData();
      }
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error(t('admin:errors.approveUserFailed'));
    }
  };

  const revokeUser = async (userId: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/revoke`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to revoke user:', error);
      toast.error(t('admin:errors.revokeUserFailed'));
    }
  };

  const handleRevokeClick = (userId: string) => {
    setUserToRevoke(userId);
    setShowRevokeModal(true);
  };

  const toggleRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';

    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to change role:', error);
      toast.error(t('admin:errors.changeRoleFailed'));
    }
  };

  const handleToggleRoleClick = (userId: string, currentRole: string) => {
    setUserToToggleRole({ id: userId, currentRole });
    setShowRoleModal(true);
  };

  const updateLimits = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(
        `/api/admin/users/${selectedUser.id}/limits`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            maxSessions: maxSessionsInput,
            maxCpuCores: maxCpuInput,
            maxMemoryGb: maxMemoryInput,
          }),
        },
      );

      if (response.ok) {
        toast.success(t('admin:messages.limitsUpdated'));
        setShowLimitsModal(false);
        setSelectedUser(null);
        fetchData();
      } else {
        const data = await response.json().catch(() => ({}));
        const rawError = String(data?.error || '').trim();
        const message = rawError.includes('maxSessions')
          ? t('admin:errors.maxSessionsInvalid')
          : rawError || t('admin:errors.updateLimitsFailed');
        toast.error(message);
      }
    } catch (error) {
      console.error('Failed to update limits:', error);
      toast.error(t('admin:errors.updateLimitsFailed'));
    }
  };

  const updateCredits = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(
        `/api/admin/users/${selectedUser.id}/credits`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            amount: creditsAmountInput,
            operation: creditsOperationInput,
          }),
        },
      );

      if (response.ok) {
        toast.success(t('admin:messages.creditsUpdated'));
        setShowCreditsModal(false);
        setSelectedUser(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.error || t('admin:errors.updateCreditsFailed'));
      }
    } catch (error) {
      console.error('Failed to update credits:', error);
      toast.error(t('admin:errors.updateCreditsFailed'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('admin:userManagementTitle')}
          description={t('admin:userManagementDescription')}
        />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:userManagementTitle')}
        description={t('admin:userManagementDescription')}
      />

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t('admin:stats.totalUsers')}
              </span>
            </div>
            <p className="text-2xl font-bold mt-2">{stats.totalUsers}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-warning" />
              <span className="text-sm text-muted-foreground">
                {t('admin:stats.pendingApproval')}
              </span>
            </div>
            <p className="text-2xl font-bold text-warning mt-2">
              {stats.pendingApproval}
            </p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-success" />
              <span className="text-sm text-muted-foreground">
                {t('admin:stats.activeUsers')}
              </span>
            </div>
            <p className="text-2xl font-bold text-success mt-2">
              {stats.activeUsers}
            </p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">
                {t('admin:stats.activeSessions')}
              </span>
            </div>
            <p className="text-2xl font-bold text-primary mt-2">
              {stats.activeSessions}
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('admin:searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          aria-label={t('admin:searchAriaLabel')}
        />
      </div>

      {/* Users Table or Empty State */}
      {users.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('admin:empty.title')}
          description={t('admin:empty.description')}
        />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin:table.user')}</TableHead>
                  <TableHead>{t('admin:table.status')}</TableHead>
                  <TableHead>{t('admin:table.role')}</TableHead>
                  <TableHead>{t('admin:table.resources')}</TableHead>
                  <TableHead>{t('admin:table.credits')}</TableHead>
                  <TableHead>{t('admin:table.usage')}</TableHead>
                  <TableHead className="text-right">
                    {t('admin:table.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {user.avatar && (
                          <img
                            src={user.avatar}
                            alt=""
                            className="h-8 w-8 rounded-full"
                          />
                        )}
                        <div>
                          <div className="font-medium">
                            {user.name || t('admin:table.noName')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.isApproved ? (
                        <Badge variant="success">
                          {t('admin:table.active')}
                        </Badge>
                      ) : (
                        <Badge variant="warning">
                          {t('admin:table.pending')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          user.role === 'admin' ? 'default' : 'secondary'
                        }
                      >
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div>
                          {t('admin:table.sessions', {
                            count: user.maxSessions,
                          })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('admin:table.resourceSummary', {
                            cpu: user.maxCpuCores,
                            memory: user.maxMemoryGb,
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        {user.unlimitedCredits ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Unlimited
                          </span>
                        ) : (
                          <div
                            className={
                              user.creditBalance <= 0
                                ? 'text-destructive font-medium'
                                : ''
                            }
                          >
                            {user.creditBalance.toLocaleString()}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {t('admin:table.used', {
                            count: Number(
                              user.totalCreditsUsed,
                            ).toLocaleString(),
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div
                          className={
                            user.activeSessionCount >= user.maxSessions
                              ? 'text-destructive font-medium'
                              : ''
                          }
                        >
                          {t('admin:table.sessionsUsage', {
                            used: user.activeSessionCount,
                            limit: user.maxSessions,
                          })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('admin:table.projects', {
                            count: user.projectCount,
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={t('admin:table.moreActions')}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!user.isApproved ? (
                            <DropdownMenuItem
                              onClick={() => approveUser(user.id)}
                              className="text-success"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              {t('admin:actions.approve')}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleRevokeClick(user.id)}
                              className="text-destructive"
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              {t('admin:actions.revokeAccess')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setMaxSessionsInput(user.maxSessions);
                              setMaxCpuInput(user.maxCpuCores);
                              setMaxMemoryInput(user.maxMemoryGb);
                              setShowLimitsModal(true);
                            }}
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            {t('admin:actions.resourceLimits')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setCreditsAmountInput(0);
                              setCreditsOperationInput('add');
                              setShowCreditsModal(true);
                            }}
                          >
                            <Key className="h-4 w-4 mr-2" />
                            {t('admin:actions.manageCredits')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleToggleRoleClick(user.id, user.role)
                            }
                          >
                            {user.role === 'admin' ? (
                              <>
                                <ShieldX className="h-4 w-4 mr-2" />
                                {t('admin:actions.removeAdmin')}
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                {t('admin:actions.makeAdmin')}
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Resource Limits Modal */}
      <Dialog open={showLimitsModal} onOpenChange={setShowLimitsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin:limitsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin:limitsDialog.description', {
                user: selectedUser?.name || selectedUser?.email || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="maxSessions">
                {t('admin:limitsDialog.maxSessions')}
              </Label>
              <Input
                id="maxSessions"
                type="number"
                min="1"
                max="10"
                value={maxSessionsInput}
                onChange={(e) =>
                  setMaxSessionsInput(parseInt(e.target.value) || 1)
                }
              />
              <p className="text-xs text-muted-foreground">
                {t('admin:limitsDialog.currentlyUsing', {
                  count: selectedUser?.activeSessionCount || 0,
                })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxCpu">{t('admin:limitsDialog.totalCpu')}</Label>
              <Input
                id="maxCpu"
                type="number"
                min="1"
                value={maxCpuInput}
                onChange={(e) => setMaxCpuInput(parseInt(e.target.value) || 1)}
              />
              <p className="text-xs text-muted-foreground">
                {t('admin:limitsDialog.sharedResourceHelp')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxMemory">
                {t('admin:limitsDialog.totalRam')}
              </Label>
              <Input
                id="maxMemory"
                type="number"
                min="1"
                value={maxMemoryInput}
                onChange={(e) =>
                  setMaxMemoryInput(parseInt(e.target.value) || 1)
                }
              />
              <p className="text-xs text-muted-foreground">
                {t('admin:limitsDialog.sharedResourceHelp')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowLimitsModal(false);
                setSelectedUser(null);
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button onClick={updateLimits}>
              {t('admin:limitsDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin:creditsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin:creditsDialog.description', {
                user: selectedUser?.name || selectedUser?.email || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted rounded-lg p-4 space-y-1">
              <p className="text-sm">
                <span className="text-muted-foreground">
                  {t('admin:creditsDialog.currentBalance')}
                </span>{' '}
                <span className="font-medium">
                  {selectedUser?.unlimitedCredits
                    ? t('admin:creditsDialog.unlimited')
                    : (selectedUser?.creditBalance || 0).toLocaleString()}
                </span>
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">
                  {t('admin:creditsDialog.totalGranted')}
                </span>{' '}
                {Number(
                  selectedUser?.totalCreditsGranted || 0,
                ).toLocaleString()}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">
                  {t('admin:creditsDialog.totalUsed')}
                </span>{' '}
                {Number(selectedUser?.totalCreditsUsed || 0).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">
                  {t('admin:creditsDialog.unlimitedCredits')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('admin:creditsDialog.unlimitedHelp')}
                </p>
              </div>
              <Button
                variant={selectedUser?.unlimitedCredits ? 'default' : 'outline'}
                size="sm"
                onClick={async () => {
                  if (!selectedUser) return;
                  try {
                    const res = await fetch(
                      `/api/admin/users/${selectedUser.id}/unlimited-credits`,
                      {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                          unlimited: !selectedUser.unlimitedCredits,
                        }),
                      },
                    );
                    if (res.ok) {
                      setSelectedUser({
                        ...selectedUser,
                        unlimitedCredits: !selectedUser.unlimitedCredits,
                      });
                      fetchData();
                    }
                  } catch {
                    /* ignore */
                  }
                }}
              >
                {selectedUser?.unlimitedCredits
                  ? t('admin:creditsDialog.enabled')
                  : t('admin:creditsDialog.disabled')}
              </Button>
            </div>
            <div className="space-y-2">
              <Label>{t('admin:creditsDialog.operation')}</Label>
              <div className="flex gap-2">
                <Button
                  variant={
                    creditsOperationInput === 'add' ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setCreditsOperationInput('add')}
                >
                  {t('admin:creditsDialog.addCredits')}
                </Button>
                <Button
                  variant={
                    creditsOperationInput === 'set' ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setCreditsOperationInput('set')}
                >
                  {t('admin:creditsDialog.setBalance')}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="creditsAmount">
                {creditsOperationInput === 'add'
                  ? t('admin:creditsDialog.creditsToAdd')
                  : t('admin:creditsDialog.newBalance')}
              </Label>
              <Input
                id="creditsAmount"
                type="number"
                min="0"
                step="0.01"
                value={creditsAmountInput}
                onChange={(e) =>
                  setCreditsAmountInput(parseFloat(e.target.value) || 0)
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreditsModal(false);
                setSelectedUser(null);
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button onClick={updateCredits}>
              {t('admin:creditsDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Access Confirmation */}
      <ConfirmDialog
        open={showRevokeModal}
        onOpenChange={(open) => {
          setShowRevokeModal(open);
          if (!open) setUserToRevoke(null);
        }}
        title={t('admin:confirm.revokeTitle')}
        description={t('admin:confirm.revokeDescription')}
        confirmLabel={t('admin:actions.revokeAccess')}
        variant="destructive"
        onConfirm={() => {
          if (userToRevoke) {
            revokeUser(userToRevoke);
            setShowRevokeModal(false);
            setUserToRevoke(null);
          }
        }}
      />

      {/* Change Role Confirmation */}
      <ConfirmDialog
        open={showRoleModal}
        onOpenChange={(open) => {
          setShowRoleModal(open);
          if (!open) setUserToToggleRole(null);
        }}
        title={t('admin:confirm.changeRoleTitle')}
        description={t('admin:confirm.changeRoleDescription', {
          role:
            userToToggleRole?.currentRole === 'admin'
              ? t('admin:confirm.user')
              : t('admin:confirm.admin'),
        })}
        confirmLabel={
          userToToggleRole?.currentRole === 'admin'
            ? t('admin:actions.removeAdmin')
            : t('admin:actions.makeAdmin')
        }
        variant="default"
        onConfirm={() => {
          if (userToToggleRole) {
            toggleRole(userToToggleRole.id, userToToggleRole.currentRole);
            setShowRoleModal(false);
            setUserToToggleRole(null);
          }
        }}
      />
    </div>
  );
}
